import React, { useEffect } from 'react';
import { FaEye, FaBullseye, FaAward, FaStar } from 'react-icons/fa';

const AboutUs = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
  }, []);

  const team = [
    {
      name: ' Dr. G Thiruvasagam',
      role: 'Founder',
      image: '/images/founder.jpg',
      bio: 'Leading disability rights advocate with 50 years of experience',
    },
  ];

  const milestones = [
    { 
      year: '1979', 
      title: 'Beginning of the Mission',
      event: 'The Society was established to serve the visually impaired, offering screenings, assistance, and rehabilitation programs.' 
    },
    { 
      year: '1990', 
      title: 'Establishment of a 150-Bed Eye Hospital',
      event: 'A fully equipped eye hospital was launched to provide affordable treatment and surgeries. For 25 years, the hospital stood as a pillar of hope before becoming non-operational in 2015.' 
    },
    { 
      year: '1995', 
      title: 'Expanding Rehabilitation in Villages',
      event: 'Our team identified visually impaired individuals from rural villages in Madurai and trained them in daily living skills, mobility and independence, and basic vocational skills. This helped them live with dignity and confidence.' 
    },
    { 
      year: '1999', 
      title: 'Supporting Livelihoods',
      event: 'The Society funded livelihood initiatives for more than 70 blind individuals including small shops, STD booths, chair caning, goat rearing and small livestock. Many beneficiaries proudly repaid the support in small amounts, showcasing their commitment and gratitude.' 
    },
    { 
      year: '2002', 
      title: 'Expansion to New Districts',
      event: 'Our operations expanded beyond Madurai to Virudhunagar, Sivagangai, and Ramanathapuram, reaching more families in need.' 
    },
    { 
      year: '2005', 
      title: 'Vocational Training for Multi-Disability Individuals',
      event: 'The Society began training people with multiple disabilities in envelope manufacturing and notebook production. These materials were supplied to LIC across India—without even requiring tender processes, highlighting the quality and trust in our work.' 
    },
    { 
      year: '2007', 
      title: 'Launch of Audiobooks for the Blind',
      event: 'A landmark initiative that transformed education for visually impaired students. These audiobooks paved the way for many to become doctors, professors, government staff, and skilled professionals.' 
    },
    { 
      year: 'Present', 
      title: 'Digital Training & Job Placements',
      event: 'With changing times, the Society conducts 3 online skill development programs every month and regular job placement drives for disabled candidates. We continue to break barriers and create opportunities for education, employment, and independence.' 
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-accent to-primary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="about-hero-title">
              About Helen Keller Society
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-95">
              Transforming lives through compassion, education, and empowerment since 1979
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6" data-testid="our-story-title">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Helen Keller Society for the Disabled was founded in 1979 with a simple yet powerful vision: 
                  to create a world where individuals with disabilities can live with dignity, independence, and joy.
                </p>
                <p>
                  Named after the legendary Helen Keller, whose life exemplified the triumph of the human spirit 
                  over adversity, our organization has been at the forefront of disability rights and inclusive 
                  development for over 45 years.
                </p>
                <p>
                  What started as a small community initiative has grown into a comprehensive support system 
                  serving thousands of individuals and families. We provide education, vocational training, 
                  healthcare support, and advocacy services to empower people with disabilities to achieve 
                  their dreams.
                </p>
                <p>
                  Today, we are proud to be one of India's leading organizations working for disability inclusion, 
                  recognized for our innovative programs and compassionate approach.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1699547283492-7a8b85165edd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxpbmRpYSUyMGRpc2FiaWxpdHl8ZW58MHx8fHwxNzY0NTY4MjMyfDA&ixlib=rb-4.1.0&q=85"
                alt="Support services"
                className="rounded-lg shadow-xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-secondary text-white p-6 rounded-lg shadow-xl">
                <p className="text-4xl font-bold">45+</p>
                <p className="text-sm">Years of Service</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 bg-warm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg" data-testid="vision-card">
              <div className="flex items-center mb-4">
                <div className="bg-primary p-3 rounded-full mr-4">
                  <FaEye className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Vision</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
               A society where every person with a disability lives with dignity, independence, and equal opportunities. 
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg" data-testid="mission-card">
              <div className="flex items-center mb-4">
                <div className="bg-secondary p-3 rounded-full mr-4">
                  <FaBullseye className="text-white text-2xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800">Our Mission</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
               A society where every person with a disability lives with dignity, independence, and equal opportunities. 
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center" data-testid="milestones-title">
            Our Journey
          </h2>
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div
                key={index}
                data-testid={`milestone-${index}`}
                className="flex items-start space-x-6 group"
              >
                <div className="flex-shrink-0">
                  {milestone.year ? (
                    <div className="bg-gradient-to-r from-primary to-secondary text-white w-20 h-20 rounded-full flex items-center justify-center font-bold text-sm shadow-lg group-hover:scale-110 transition-transform">
                      {milestone.year}
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-accent to-primary text-white w-20 h-20 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <FaStar className="text-3xl" />
                    </div>
                  )}
                </div>
                <div className="flex-grow">
                  <div className="bg-warm p-6 rounded-lg shadow-md group-hover:shadow-xl transition-all">
                    <h4 className="text-xl font-bold text-gray-800 mb-2">{milestone.title}</h4>
                    <p className="text-gray-700 leading-relaxed">{milestone.event}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-16 bg-warm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4" data-testid="team-title">
              Meet Our Team
            </h2>
            <p className="text-gray-600 text-lg">
              Dedicated professionals committed to making a difference
            </p>
          </div>

          <div className="flex justify-center">
            <div className="max-w-xs w-full">
              {team.map((member, index) => (
                <div
                  key={index}
                  data-testid={`team-member-${index}`}
                  className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-55 object-cover"
                  />
                  <div className="p-6 text-center">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">{member.name}</h3>
                    <p className="text-primary font-semibold mb-3">{member.role}</p>
                    <p className="text-gray-600 text-sm">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <FaAward className="text-6xl text-secondary mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Awards & Recognition</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-warm p-6 rounded-lg text-center">
              <h4 className="font-bold text-gray-800 mb-2">Best NGO Award 2018</h4>
              <p className="text-gray-600 text-sm">Ministry of Social Justice</p>
            </div>
            <div className="bg-warm p-6 rounded-lg text-center">
              <h4 className="font-bold text-gray-800 mb-2">Excellence in Disability Services 2020</h4>
              <p className="text-gray-600 text-sm">National Disability Council</p>
            </div>
            <div className="bg-warm p-6 rounded-lg text-center">
              <h4 className="font-bold text-gray-800 mb-2">Community Impact Award 2023</h4>
              <p className="text-gray-600 text-sm">UN Sustainable Development Goals</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;